import React from 'react';
import moment from 'moment';
import {
  formatDateTime,
  getUserDateFormat,
  getUserTimeZoneConfig
} from '../../../Utils/dateFormat';
import CascadeLogo from '../../../Assets/Icons/CascadeLogo';

/**
 * Compact note PDF header shared by the web Note PDF, Audit and Note with
 * Audit PDFs (TRPA-8098). Mirrors the backend emr-note-pdf.html header:
 * logo row, 2px rule and a 4×3 inline "Label: value" grid.
 *
 * No letter-spacing on the uppercase labels: the backend PDF engine renders
 * them untracked, and html2canvas splits tracked text into uneven gaps.
 */

const EMPTY = '-';

export const S = {
  tag: { textAlign: 'right', lineHeight: 1.35, verticalAlign: 'middle' },
  kind: {
    display: 'block',
    fontSize: '8.5px',
    fontWeight: 600,
    textTransform: 'uppercase',
    color: '#1A7DC4'
  },
  by: { fontSize: '10px', color: '#6B7A8D' },
  byValue: { fontWeight: 600, color: '#1A2332' },
  rule: {
    height: 2,
    background: '#47A4EA',
    margin: '8px 0 0',
    fontSize: 1,
    lineHeight: '1px'
  },
  meta: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    margin: '4px 0 0'
  },
  metaTd: {
    width: '33.33%',
    padding: '5px 12px 5px 0',
    verticalAlign: 'top',
    borderBottom: '1px solid #F0F2F5',
    lineHeight: 1.4
  },
  k: {
    fontSize: '8.5px',
    fontWeight: 600,
    textTransform: 'uppercase',
    color: '#6B7A8D'
  },
  v: { fontSize: '10.5px', fontWeight: 500, color: '#1A2332' },
  date: { display: 'inline-block', whiteSpace: 'nowrap' }
};

const clean = value => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const orEmpty = value => clean(value) || EMPTY;

// Calendar dates (DOB, admission) must never shift a day, so the date part of
// an ISO value is taken as-is instead of being converted to local time.
export const formatCalendarDate = value => {
  const raw = clean(value);
  if (!raw) return EMPTY;
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) {
    const m = moment(match[1], 'YYYY-MM-DD', true);
    if (m.isValid()) return m.format(getUserDateFormat());
  }
  return raw;
};

// Date + time in the viewer's zone, followed by the zone label (e.g. IST).
export const formatZonedDateTime = value => {
  const raw = clean(value);
  if (!raw) return EMPTY;
  const input = /^\d+$/.test(raw) ? Number(raw) : raw;
  const formatted = formatDateTime(input);
  if (!formatted) return raw;
  // Label the zone for the same instant so DST matches the formatted time.
  const { displayName, utcOffset } = getUserTimeZoneConfig(
    moment(input).toDate()
  );
  const label = displayName || (utcOffset ? `UTC${utcOffset}` : 'UTC');
  return `${formatted} ${label}`;
};

// Codes only, each once, original order.
export const formatCptCodes = codes => {
  const seen = [];
  (codes || []).forEach(c => {
    const code = clean(typeof c === 'string' ? c : c?.cptCode);
    if (code && !seen.includes(code)) seen.push(code);
  });
  return seen.length ? seen.join(', ') : EMPTY;
};

/**
 * Resolves every header value from the data the web already has loaded:
 * `header` (GET /clinic/job/info-to-print), `patient` (GET /clinic/patient)
 * and `session` (the audit list row).
 */
export const buildNotePdfHeader = ({ header, patient, session } = {}) => {
  const progressNote = header?.progressNote || session?.progressNote || null;
  const practitionerNote =
    header?.practitionerNote || session?.practitionerNote || null;
  const pccNote = progressNote || practitionerNote;
  const noteKind =
    !progressNote && practitionerNote ? 'Practitioner Note' : 'Progress Note';
  const demographics = header?.patientDemographics || {};

  return {
    // Only a note that went to the EHR has a PCC note (kind, creator, id).
    isEhrSubmitted: Boolean(pccNote),
    noteKind,
    createdBy: orEmpty(pccNote?.createdBy),
    workType: orEmpty(
      pccNote?.progressNoteType || header?.workType || session?.workType
    ),
    provider: orEmpty(header?.author || session?.author),
    jobNumber: orEmpty(session?.jobNumber || header?.jobNumber),
    facility: orEmpty(
      header?.facility?.facilityName || session?.facility?.facilityName
    ),
    patient: orEmpty(
      header?.appointmentDetails?.fullName ||
        header?.patientName ||
        session?.patientName
    ),
    dob: formatCalendarDate(
      patient?.birthDate ||
        demographics.dateOfBirth ||
        demographics.birthDate ||
        header?.appointmentDetails?.dateOfBirth
    ),
    mrn: orEmpty(
      patient?.medicalRecordNumber || header?.medicalRecordNumber
    ),
    room: orEmpty(patient?.roomDesc || demographics.roomDesc),
    admissionDate: formatCalendarDate(
      header?.admissionDate ||
        patient?.admissionDate ||
        demographics.admissionDate
    ),
    encounterDate: formatZonedDateTime(
      pccNote?.effectiveDate || header?.effectiveDate || session?.effectiveDate
    ),
    template: clean(header?.dictationTemplate?.templateName) || 'None',
    cptCodes: formatCptCodes(
      session?.cptCodesInContent?.length
        ? session.cptCodesInContent
        : header?.cptCodesInContent
    ),
    noteId: orEmpty(pccNote?.noteId || pccNote?.progressNoteId)
  };
};

export const buildNotePdfFooter = fields => {
  const generated = `Generated ${formatZonedDateTime(Date.now())}`;
  return fields.isEhrSubmitted
    ? `${generated} · ${fields.noteKind} #${fields.noteId}`
    : generated;
};

const Cell = ({ label, value, isDate }) => (
  <td style={S.metaTd}>
    <span style={S.k}>{label}:</span>{' '}
    <span style={isDate ? { ...S.v, ...S.date } : S.v}>{value}</span>
  </td>
);

const NotePdfHeader = ({ fields }) => (
  <>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        <tr>
          <td style={{ padding: 0, verticalAlign: 'middle' }}>
            <div style={{ height: 24, width: 89 }}>
              <CascadeLogo />
            </div>
          </td>
          <td style={S.tag}>
            {fields.isEhrSubmitted ? (
              <>
                <span style={S.kind}>{fields.noteKind}</span>
                <span style={S.by}>
                  Source: <b style={S.byValue}>PointClickCare EHR</b> · Created
                  By: <b style={S.byValue}>{fields.createdBy}</b>
                </span>
              </>
            ) : (
              <span style={S.by}>
                Source: <b style={S.byValue}>CascadeMD</b>
              </span>
            )}
          </td>
        </tr>
      </tbody>
    </table>
    <div style={S.rule} />
    <table style={S.meta}>
      <tbody>
        <tr>
          <Cell label='Work Type' value={fields.workType} />
          <Cell label='Provider' value={fields.provider} />
          <Cell label='CascadeMD Job #' value={fields.jobNumber} />
        </tr>
        <tr>
          <Cell label='Facility' value={fields.facility} />
          <Cell label='Patient' value={fields.patient} />
          <Cell label='DOB' value={fields.dob} isDate />
        </tr>
        <tr>
          <Cell label='MRN' value={fields.mrn} />
          <Cell label='Room' value={fields.room} />
          <Cell label='Date of Admission' value={fields.admissionDate} isDate />
        </tr>
        <tr>
          <Cell label='Date of Encounter' value={fields.encounterDate} isDate />
          <Cell label='Template' value={fields.template} />
          <Cell label='CPT Code' value={fields.cptCodes} />
        </tr>
      </tbody>
    </table>
  </>
);

export default NotePdfHeader;
